import { useApolloClient } from '@apollo/client';
import {
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonSpinner,
  IonTabBar,
  IonTabButton,
  IonTabs,
  useIonViewDidEnter,
} from '@ionic/react';
import {
  chatbubbles,
  chatbubblesOutline,
  ellipsisHorizontal,
  ellipsisHorizontalOutline,
  home,
  homeOutline,
} from 'ionicons/icons';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
  SubscribeToMachineStateDocument,
  SubscribeToMachineStateSubscription,
  SubscribeToMachineStateSubscriptionVariables,
  useGetTeamQuery,
} from '../../api/graphql/generated';
import { subscriptionDefaultErrorHandler } from '../../api/subscription/apollo-subscription.manager';
import { beaconIcon } from '../../components/logo/beacon-icon';
import i18n from '../../i18n';
import { useBeaconRouter } from '../../route/route.hooks';
import { ConfigureChartPage } from '../chart/configure-chart.page';
import { SelectChartTemplatePage } from '../chart/select-team-template.page';
import { ChatThreadPage } from '../chat/chat-thread.page';
import { ChatPage } from '../chat/chat.page';
import { FileVerionsPage } from '../machine/file-versions.page';
import { MachineApplicationFormPage } from '../machine/machine-application-form.page';
import { MachineApplicationsPage } from '../machine/machine-applications.page';
import { MachineDetailsPage } from '../machine/machine-details.page';
import { MachineEventDefinitionPage } from '../machine/machine-event-definition.page';
import { MachineEventsPage } from '../machine/machine-events.page';
import { MachineFilesPage } from '../machine/machine-files.page';
import { MachineIOPage } from '../machine/machine-io.page';
import { MachineLogsPage } from '../machine/machine-logs.page';
import { MachineMaintenancePage } from '../machine/machine-maintenance.page';
import { MachineRealtimePage } from '../machine/machine-realtime.page';
import { MachineScratchpadPage } from '../machine/machine-scratchpad.page';
import { MachineVariablesPage } from '../machine/machine-variables.page';
import { MachinePage } from '../machine/machine.page';
import { Page } from '../page';
import { buildRedirectHistoryState } from '../redirect.hooks';
import { TeamHomePage } from './team-home.page';
import { TeamMachinesPage } from './team-machines.page';
import { TeamMorePage } from './team-more.page';
import { TeamSetupPage } from './team-setup.page';
import { TeamUsersPage } from './team-users.page';

interface TeamsTabsProps {
  id: string;
}

interface TabButton {
  name: string;
  icon: string;
  altIcon: string;
  path: string;
}

const tabs: TabButton[] = [
  {
    name: i18n.t('common:home'),
    path: 'home',
    icon: home,
    altIcon: homeOutline,
  },
  {
    name: i18n.t('common:robots'),
    path: 'machines',
    icon: beaconIcon,
    altIcon: beaconIcon,
  },
  {
    name: i18n.t('common:chat'),
    path: 'chat',
    icon: chatbubbles,
    altIcon: chatbubblesOutline,
  },
  {
    name: i18n.t('common:more'),
    path: 'more',
    icon: ellipsisHorizontal,
    altIcon: ellipsisHorizontalOutline,
  },
];

const TeamTabs = (props: TeamsTabsProps): ReactElement => {
  const { id } = props;

  const { data: teamData } = useGetTeamQuery({
    variables: { input: { id } },
    fetchPolicy: 'cache-first',
  });

  const [selectedTab, setSelectedTab] = useState<string>();

  const { builder } = useBeaconRouter();
  const { replace, location } = useHistory();

  const baseURL = builder.getTeamRoute(id);
  const initialTab = selectedTab ?? 'home';

  /**
   * When switching teams we see an issue where the redirect leveraged inside the IonRouterOutlet
   * doesn't change the url back to /home as expected. This hook makes sure that we reflect
   * the appropriate url when changing teams. The redirect is still used within the router outlet
   * to ensure that other functionality within ion tabs works as expected.
   */
  useEffect(() => {
    if (location.pathname === baseURL || location.pathname === `${baseURL}/`) {
      replace(`${baseURL}/${initialTab}`);
    }
  }, [location.pathname, baseURL, replace, initialTab]);

  const checkAndSetMatchedTab = useCallback(
    (path: string) => {
      const match = extractTabMatch(tabs, path, baseURL);
      if (match) {
        setSelectedTab(match);
      }
    },
    [baseURL]
  );

  useIonViewDidEnter(() => {
    if (selectedTab === undefined) {
      const activePage = document.querySelector(
        '.ion-page:not(.ion-page-hidden)'
      );
      const backButton = activePage?.querySelector('ion-back-button');
      const backbuttonHref = backButton?.getAttribute('defaulthref');
      if (backbuttonHref) {
        checkAndSetMatchedTab(backbuttonHref);
      }
    }
  });

  useEffect(() => {
    checkAndSetMatchedTab(location.pathname);
  }, [checkAndSetMatchedTab, location.pathname]);

  const client = useApolloClient();

  useEffect(() => {
    const subscription = client
      .subscribe<
        SubscribeToMachineStateSubscription,
        SubscribeToMachineStateSubscriptionVariables
      >({
        query: SubscribeToMachineStateDocument,
        variables: {
          input: {
            teamId: id,
          },
        },
      })
      .subscribe({
        error: subscriptionDefaultErrorHandler,
      });

    return () => {
      subscription?.unsubscribe();
    };
  }, [client, id]);

  const chatId = teamData?.getTeam.chatId;

  const homeRoute = builder.getTeamHomeRoute(id);

  return (
    <IonTabs>
      <IonRouterOutlet key={props.id}>
        <Page
          path={homeRoute}
          render={(renderProps) => {
            return <TeamHomePage {...renderProps} id={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_home'}
        />
        <Page
          path={builder.getTeamChartRoute(id, ':id')}
          exact={true}
          i18nTitleKey={'common:page_team_home'}
        >
          <ConfigureChartPage teamId={id} />
        </Page>
        <Page
          path={builder.getTeamChartTemplatesRoute(id)}
          exact={true}
          i18nTitleKey={'common:page_team_home'}
        >
          <SelectChartTemplatePage
            parentId={id}
            parentObject={'team'}
            defaultBackRoute={homeRoute}
            routerBuilder={(templateId: string) => {
              return builder.getTeamChartRoute(id, 'new', {
                queryParams: {
                  templateId,
                },
              });
            }}
            historyState={() => {
              return buildRedirectHistoryState([homeRoute]);
            }}
          />
        </Page>
        <Page
          path={builder.getTeamChatRoute(id)}
          render={(renderProps) => {
            if (chatId) {
              return <ChatPage {...renderProps} id={chatId} teamId={id} />;
            } else {
              return (
                <div className="full-height-width content-center">
                  <IonSpinner className="ion-align-self-center" />
                </div>
              );
            }
          }}
          exact={true}
          i18nTitleKey={'common:page_team_chat'}
        />
        <Page
          path={builder.getTeamChatThreadRoute(id, ':threadId')}
          render={() => {
            if (chatId) {
              return (
                <ChatThreadPage
                  defaultBackRoute={builder.getTeamChatRoute(id)}
                  chatId={chatId}
                />
              );
            } else {
              return (
                <div className="full-height-width content-center">
                  <IonSpinner className="ion-align-self-center" />
                </div>
              );
            }
          }}
        />
        <Page
          path={builder.getMachinesRoute(id)}
          exact={true}
          i18nTitleKey={'common:page_team_machines'}
        >
          <TeamMachinesPage id={id} />
        </Page>
        <Page
          path={builder.getMachineDetailsRoute(id, ':id')}
          render={(renderProps) => {
            return <MachineDetailsPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine'}
        />
        <Page
          path={builder.getMachineMaintenanceRoute(id, ':id')}
          render={(renderProps) => {
            return <MachineMaintenancePage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine'}
        />
        <Page
          path={builder.getMachineEventDefinitionsRoute(id, ':machineId')}
          render={(renderProps) => {
            return <MachineEventsPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_events'}
        />
        <Page
          path={builder.getMachineApplicationsRoute(id, ':machineId')}
          render={(renderProps) => {
            return <MachineApplicationsPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_applications'}
        />
        <Page
          path={builder.getMachineApplicationRoute(
            id,
            ':machineId',
            ':applicationId'
          )}
          render={(renderProps) => {
            return <MachineApplicationFormPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_application_form'}
        />
        <Page
          path={builder.getMachineScratchpadRoute(id, ':machineId', ':id')}
          render={(renderProps) => {
            return <MachineScratchpadPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_events'}
        />
        <Page
          path={builder.getMachineEventDefinitionRoute(id, ':machineId', ':id')}
          render={(renderProps) => {
            return <MachineEventDefinitionPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_event_details'}
        />
        <Page
          path={builder.getMachineFilesRoute(id, ':id')}
          render={(renderProps) => {
            return <MachineFilesPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_files'}
        />
        <Page
          path={builder.getMachineFileRoute(id, ':id', ':fileId')}
          render={(renderProps) => {
            return <MachineFilesPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_files'}
        />
        <Page
          path={builder.getMachineFileRoute(id, ':id', '(.*)/versions')}
          render={(renderProps) => {
            return <FileVerionsPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_file_versions'}
        />
        <Page
          // Somewhat fragile, but we're looking for fileId matches of exactly 3 characters
          // We don't care about the fileId since we're using the query param `path` but we're
          // looking for an exactly path length match here to distinguish from the `versions`
          // match above
          path={builder.getMachineFileRoute(id, ':id', '(.*)/:fileId(...)')}
          render={(renderProps) => {
            return <MachineFilesPage {...renderProps} teamId={id} />;
          }}
          exact={true}
          i18nTitleKey={'common:page_team_machine_files'}
        />
        <Page
          path={builder.getMachineVariablesRoute(id, ':id')}
          i18nTitleKey={'common:page_team_machine_variables'}
        >
          <MachineVariablesPage teamId={id} />
        </Page>
        <Page
          path={builder.getMachineIORoute(id, ':id')}
          i18nTitleKey={'common:page_team_machine_io'}
        >
          <MachineIOPage teamId={id} />
        </Page>
        <Page
          path={builder.getMachineLogsRoute(id, ':id')}
          i18nTitleKey={'common:page_team_machine_logs'}
        >
          <MachineLogsPage teamId={id} />
        </Page>
        <Page
          path={builder.getMachineRealtimeRoute(id, ':id')}
          i18nTitleKey={'common:page_team_machine_realtime'}
        >
          <MachineRealtimePage teamId={id} />
        </Page>
        <Page
          path={builder.getMachineRoute(id, ':id')}
          exact={true}
          i18nTitleKey={'common:page_team_machine'}
        >
          <MachinePage teamId={id} />
        </Page>
        <Page
          path={builder.getTeamUsersRoute(id)}
          exact={true}
          i18nTitleKey={'common:page_team_users'}
        >
          <TeamUsersPage id={id} />
        </Page>
        <Page
          path={builder.getTeamMoreRoute(id)}
          exact={true}
          i18nTitleKey={'common:page_team_more'}
        >
          <TeamMorePage id={id} />
        </Page>
        <Page
          path={builder.getTeamSetupRoute(id)}
          exact={true}
          i18nTitleKey={'common:page_team_setup'}
        >
          <TeamSetupPage id={id} />
        </Page>
      </IonRouterOutlet>
      <IonTabBar slot="bottom">
        {generateTabs(tabs, baseURL, selectedTab)}
      </IonTabBar>
    </IonTabs>
  );
};

const generateTabs = (
  tabs: TabButton[],
  pathPrefix: string,
  selected?: string
): ReactElement[] => {
  return tabs.map((tab, index) => {
    return (
      <IonTabButton
        key={index}
        tab={tab.path}
        href={`${pathPrefix}/${tab.path}`}
        selected={selected ? selected === tab.path : undefined}
      >
        <IonIcon icon={selected === tab.path ? tab.icon : tab.altIcon} />
        <IonLabel>{tab.name}</IonLabel>
      </IonTabButton>
    );
  });
};

const extractTabMatch = (
  tabs: TabButton[],
  path: string,
  pathPrefix: string
): string | null => {
  const trimmedPrefix = pathPrefix.replace(/\/$/, '');
  let match: string | null = null;
  for (const tab in tabs) {
    const trimmedPath = tabs[tab].path.replace(/^\//, '');
    if (path.startsWith(`${trimmedPrefix}/${trimmedPath}`)) {
      match = tabs[tab].path;
      break;
    }
  }
  return match;
};

export { TeamTabs };
